import React, {
  lazy,
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

import ScreenRenderer from "./ScreenRenderer";
import CompanyBrandProvider from "./CompanyBrandProvider";
import PoweredByApruv from "../components/common/PoweredByApruv";
import { loadImageHref, setDocumentLink } from "../branding/favicon";
import { resolveNextScreenIndex } from "./routing";
import type { FlowActionEvent, FlowConfig, FlowValues } from "./types";

const SuccessConfetti = lazy(() => import("./components/SuccessConfetti"));

interface FlowRendererProps {
  completionMessage?: string;
  flow: FlowConfig;
  initialValues?: FlowValues;
  initialScreenId?: string;
  onAction?: (action: FlowActionEvent) => void;
  onComplete?: (values: FlowValues) => void;
  beforeNavigate?: (values: FlowValues) => Promise<void>;
}

const persistentContextComponentTypes = new Set([
  "topBar",
  "h1",
  "h2",
  "h3",
  "h4",
  "subheader",
  "proofOfServiceMedia",
  "notes",
  "videoPlayer",
  "visualAsset",
  "spacer",
]);

const implicitServiceContextComponentTypes = new Set([
  "topBar",
  "proofOfServiceMedia",
  "notes",
  "videoPlayer",
]);

const hasRatingComponent = (screen: FlowConfig["screens"][number]) =>
  screen.components.some((component) =>
    ["csatForm", "npsForm"].includes(component.type),
  );

const hasPersistentServiceContext = (screen: FlowConfig["screens"][number]) =>
  screen.components.some(
    (component) =>
      component.persistent &&
      persistentContextComponentTypes.has(component.type),
  ) ||
  (hasRatingComponent(screen) &&
    screen.components.some(
      (component) => component.type === "proofOfServiceMedia",
    ));

const FlowRenderer: React.FC<FlowRendererProps> = ({
  completionMessage = "Thank you. Your feedback has been submitted.",
  flow,
  initialValues,
  initialScreenId,
  onAction,
  onComplete,
  beforeNavigate,
}) => {
  const embedded = window.self !== window.top;
  const initialScreenIndex = useMemo(() => {
    const requestedScreenId = initialScreenId ?? flow.entryScreenId;
    if (!requestedScreenId) return 0;
    const foundIndex = flow.screens.findIndex(
      (screen) => screen.id === requestedScreenId,
    );
    return foundIndex >= 0 ? foundIndex : 0;
  }, [flow.entryScreenId, flow.screens, initialScreenId]);

  const screenIndexById = useMemo(
    () => new Map(flow.screens.map((screen, index) => [screen.id, index])),
    [flow.screens],
  );
  const [screenHistory, setScreenHistory] = useState([initialScreenIndex]);
  const [values, setValues] = useState<FlowValues>(initialValues ?? {});
  const valuesRef = useRef<FlowValues>(initialValues ?? {});
  const [completed, setCompleted] = useState(false);

  const navigationPending = useRef(false);
  const [pending, setPending] = useState(false);
  const [navigationError, setNavigationError] = useState<string | null>(null);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const acknowledgedNavigation = (nextValues: FlowValues, commit: () => void) => {
    if (navigationPending.current) return;
    if (!beforeNavigate) {
      commit();
      return;
    }
    navigationPending.current = true;
    setPending(true);
    setNavigationError(null);
    void (async () => {
      try {
        await beforeNavigate(nextValues);
        if (alive.current) commit();
      } catch {
        if (alive.current)
          setNavigationError(
            'We could not save your feedback. Your answers are still here. Please try again.'
          );
      } finally {
        navigationPending.current = false;
        if (alive.current) setPending(false);
      }
    })();
  };

  useEffect(() => {
    let cancelled = false;
    loadImageHref(flow.brand.faviconUrl).then((href) => {
      if (!cancelled && href) setDocumentLink("icon", href);
    });

    return () => {
      cancelled = true;
    };
  }, [flow.brand.faviconUrl]);

  const activeScreenIndex =
    screenHistory[screenHistory.length - 1] ?? initialScreenIndex;
  const activeScreen = flow.screens[activeScreenIndex] ?? flow.screens[0];
  const persistenceSourceIndex = useMemo(() => {
    if (initialScreenId) {
      const visited = new Set<number>();
      const queue = [initialScreenIndex];
      while (queue.length > 0) {
        const candidateIndex = queue.shift()!;
        if (visited.has(candidateIndex)) continue;
        visited.add(candidateIndex);
        const candidate = flow.screens[candidateIndex];
        if (
          candidate &&
          (candidate.presentation ?? "default") === "default" &&
          hasPersistentServiceContext(candidate)
        ) {
          return candidateIndex;
        }
        const candidateId = candidate?.id;
        if (!candidateId) continue;
        flow.screens.forEach((screen, screenIndex) => {
          if (screen.routes?.some((route) => route.targetScreenId === candidateId)) {
            queue.push(screenIndex);
          }
        });
      }

      // CX Studio may remount a destination preview without serializing the
      // decision-node route history. The rating screen remains the
      // authoritative source of persistent service context in that payload.
      const ratingContextIndex = flow.screens.findIndex(
        (screen) =>
          (screen.presentation ?? "default") === "default" &&
          hasRatingComponent(screen) && hasPersistentServiceContext(screen),
      );
      if (ratingContextIndex >= 0) return ratingContextIndex;
    }

    // Persistence belongs to the first eligible screen the respondent actually
    // visited. Picking the first persistent screen in the config leaks context
    // from unrelated branches (for example, an "already submitted" path).
    return screenHistory.find((screenIndex) => {
      const screen = flow.screens[screenIndex];
      return (
        screen &&
        (screen.presentation ?? "default") === "default" &&
        hasPersistentServiceContext(screen)
      );
    }) ?? -1;
  }, [flow.screens, initialScreenId, initialScreenIndex, screenHistory]);
  const persistentComponents = useMemo(() => {
    const entryScreen = flow.screens[persistenceSourceIndex];
    if (!entryScreen || (entryScreen.presentation ?? "default") !== "default") {
      return [];
    }

    const explicitlyPersistent = entryScreen.components.filter(
      (component) =>
        component.persistent &&
        persistentContextComponentTypes.has(component.type),
    );
    if (explicitlyPersistent.length > 0) {
      // The organization header is structural page chrome. Keep it with an
      // explicitly persistent proof bundle even when older experience data did
      // not mark the top-bar block itself as persistent; otherwise it remains
      // in the routed screen and appears below proof and notes.
      return entryScreen.components.filter(
        (component) =>
          component.type === "topBar" || explicitlyPersistent.includes(component),
      );
    }

    // Older CX Studio experience versions predate the persistence setting.
    // Keep their proof-of-service bundle mounted across a rating route so the
    // customer experiences one continuous page. Explicit settings above remain
    // authoritative for newly configured experiences.
    if (hasRatingComponent(entryScreen)) {
      return entryScreen.components.filter((component) =>
        implicitServiceContextComponentTypes.has(component.type),
      );
    }

    return [];
  }, [flow.screens, persistenceSourceIndex]);
  const persistentComponentIds = useMemo(
    () => new Set(persistentComponents.map((component) => component.id)),
    [persistentComponents],
  );
  const activeExcludedComponentIds = useMemo(() => {
    const excluded = new Set(
      activeScreenIndex === persistenceSourceIndex ? persistentComponentIds : [],
    );
    // A retained page header owns the branding across all routed screens,
    // even when those screens configure their own header with a different ID.
    if (persistentComponents.some((component) => component.type === "topBar")) {
      activeScreen.components.forEach((component) => {
        if (component.type === "topBar") excluded.add(component.id);
      });
    }
    return excluded;
  }, [activeScreen, activeScreenIndex, persistenceSourceIndex, persistentComponentIds, persistentComponents]);
  const isFirstScreen = screenHistory.length <= 1;
  const isLastScreen =
    activeScreen.routes !== undefined
      ? activeScreen.routes.length === 0
      : activeScreenIndex === flow.screens.length - 1;
  const shouldShowBack = activeScreen.showBack ?? !isFirstScreen;
  const activeScreenLabel = activeScreen.previewLabel?.trim().toLowerCase();
  const excludesCelebration =
    activeScreenLabel === "response already submitted" ||
    activeScreenLabel === "recovery confirmation";
  const shouldCelebrate =
    flow.celebrationEffect === "confetti" &&
    !excludesCelebration &&
    (completed ||
      activeScreen.components.some(
        (component) =>
          component.type === "reviewSelection" ||
          (component.type === "visualAsset" &&
            component.preset === "success_check"),
      ));

  const setValue = (componentId: string, value: unknown) => {
    if (navigationPending.current) return;
    const nextValues = { ...valuesRef.current, [componentId]: value };
    valuesRef.current = nextValues;
    setValues(nextValues);
  };

  const emitAction = (
    action: FlowActionEvent,
    actionValues = valuesRef.current,
  ) => {
    onAction?.({
      ...action,
      metadata: {
        ...(action.metadata ?? {}),
        values: actionValues,
      },
    });
  };

  const goBack = (fallbackScreenId?: string) => {
    if (navigationPending.current) return;
    setCompleted(false);
    setScreenHistory((currentHistory) => {
      if (currentHistory.length > 1) {
        return currentHistory.slice(0, -1);
      }

      const activeIndex =
        currentHistory[currentHistory.length - 1] ?? initialScreenIndex;
      const activeId = flow.screens[activeIndex]?.id;
      const reverseRouteSources = flow.screens
        .filter((screen) =>
          screen.routes?.some((route) => route.targetScreenId === activeId),
        )
        .map((screen) => screen.id);
      const fallbackCandidates = [
        fallbackScreenId,
        ...reverseRouteSources,
      ].filter((candidate): candidate is string => Boolean(candidate));

      for (const candidate of fallbackCandidates) {
        const candidateIndex = screenIndexById.get(candidate);
        if (candidateIndex !== undefined && candidateIndex !== activeIndex) {
          return [candidateIndex];
        }
      }

      return currentHistory;
    });
  };

  const continueWithValues = (nextValues: FlowValues) => {
    const nextScreenIndex = resolveNextScreenIndex({
      activeScreen,
      activeScreenIndex,
      routeContext: flow.routeContext,
      screenIndexById,
      screens: flow.screens,
      values: nextValues,
    });

    if (nextScreenIndex === null) {
      setCompleted(true);
      emitAction(
        {
          componentId: activeScreen.id,
          type: "complete",
          value: true,
        },
        nextValues,
      );
      onComplete?.(nextValues);
      return;
    }

    setCompleted(false);
    setScreenHistory((currentHistory) => [...currentHistory, nextScreenIndex]);
  };

  const goNext = () => {
    const nextValues = valuesRef.current;
    acknowledgedNavigation(nextValues, () => continueWithValues(nextValues));
  };

  const goToScreen = (screenId: string) => {
    const nextScreenIndex = screenIndexById.get(screenId);
    if (nextScreenIndex === undefined || nextScreenIndex === activeScreenIndex) return;

    acknowledgedNavigation(valuesRef.current, () => {
      setCompleted(false);
      setScreenHistory((currentHistory) => [...currentHistory, nextScreenIndex]);
    });
  };

  const submitValueAndGoNext = (componentId: string, value: unknown) => {
    const nextValues = { ...valuesRef.current, [componentId]: value };
    acknowledgedNavigation(nextValues, () => {
      valuesRef.current = nextValues;
      setValues(nextValues);
      emitAction(
        {
          componentId,
          type: 'continue',
          value: true,
        },
        nextValues
      );
      continueWithValues(nextValues);
    });
  };

  return (
    <CompanyBrandProvider brand={flow.brand}>
      <Box
        data-testid="flow-page"
        sx={{
          alignItems: "stretch",
          bgcolor: "var(--cx-brand-bg)",
          color: "var(--cx-brand-text)",
          display: "flex",
          justifyContent: "center",
          minHeight: "100dvh",
          width: "100%",
          '[data-cx-color-scheme="dark"] &': {
            backgroundColor: 'var(--cx-brand-bg)',
            backgroundImage:
              'radial-gradient(circle at 50% -10%, color-mix(in srgb, var(--cx-brand-primary) 18%, transparent), transparent 42%)',
            py: embedded ? 0 : { xs: 0, sm: 5 },
          },
        }}
      >
        <Stack
          data-testid="flow-viewport"
          spacing={0}
          sx={{
            bgcolor: "var(--cx-brand-bg)",
            boxShadow: {
              xs: "none",
              sm: "0 0 0 1px rgba(145, 158, 171, 0.12)",
            },
            maxWidth: 393,
            minHeight: "100dvh",
            overflow: "hidden",
            position: "relative",
            width: "100%",
            '[data-cx-color-scheme="dark"] &': {
              backdropFilter: 'blur(24px)',
              backgroundColor: 'var(--cx-brand-bg)',
              border: { xs: 0, sm: '1px solid var(--cx-brand-border)' },
              borderRadius: { xs: 0, sm: 7 },
              boxShadow: { xs: 'none', sm: '0 28px 80px rgba(0, 0, 0, 0.32)' },
              minHeight: embedded
                ? '100dvh'
                : { xs: '100dvh', sm: 'calc(100dvh - 80px)' },
            },
          }}
        >
          {shouldCelebrate ? (
            <Suspense fallback={null}>
              <SuccessConfetti
                key={`${activeScreen.id}-${completed ? "complete" : "review"}`}
              />
            </Suspense>
          ) : null}

          {navigationError ? <Alert severity="error">{navigationError}</Alert> : null}
          {pending ? <Alert severity="info">Saving your feedback…</Alert> : null}
          <fieldset disabled={pending} style={{ display: "contents" }}>
          {persistentComponents.length > 0 ? (
            <ScreenRenderer
              goBack={goBack}
              goNext={goNext}
              goToScreen={goToScreen}
              isLastScreen={isLastScreen}
              onAction={emitAction}
              persistentContext
              screen={{
                components: persistentComponents,
                hideFooterAction: true,
                id: "persistent-context",
              }}
              setValue={setValue}
              submitValueAndGoNext={submitValueAndGoNext}
              values={values}
            />
          ) : null}

          <ScreenRenderer
            excludeComponentIds={activeExcludedComponentIds}
            goBack={goBack}
            goNext={goNext}
            goToScreen={goToScreen}
            isLastScreen={isLastScreen}
            onAction={emitAction}
            screen={activeScreen}
            setValue={setValue}
            submitValueAndGoNext={submitValueAndGoNext}
            values={values}
          />

          </fieldset>
          {completed && activeScreen.presentation !== "confirmation_card" ? (
            <Card
              sx={{
                bgcolor: "var(--cx-brand-primary-soft)",
                border: "1px solid var(--cx-brand-primary)",
                boxShadow: "none",
                mx: 2,
                p: 1.5,
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: "var(--cx-brand-primary-dark)", fontWeight: 800 }}
              >
                {completionMessage}
              </Typography>
            </Card>
          ) : null}

          <Box sx={{ flexGrow: 1 }} />

          {activeScreen.hideFooterAction ? (
            <PoweredByApruv compact sx={{ pb: 2 }} />
          ) : null}

          {!activeScreen.hideFooterAction ? (
            <Card
              sx={{
                bgcolor: "var(--cx-brand-surface)",
                border: 0,
                borderRadius: 0,
                bottom: 0,
                color: "var(--cx-brand-text)",
                boxShadow: "none",
                px: 2,
                py: 2,
                position: "sticky",
                '[data-cx-color-scheme="dark"] &': {
                  backdropFilter: 'blur(18px)',
                  background: 'color-mix(in srgb, var(--cx-brand-surface) 78%, transparent)',
                  borderTop: '1px solid var(--cx-brand-border)',
                },
              }}
            >
              <Stack spacing={1.25}>
                <Stack direction="row" spacing={1.25}>
                  {shouldShowBack ? (
                    <Button
                      color="inherit"
                      fullWidth
                      onClick={() => goBack()}
                      sx={{
                        bgcolor: "var(--cx-brand-button-bg)",
                        borderColor: "var(--cx-brand-button-border)",
                        color: "var(--cx-brand-button-text)",
                        "&:hover": {
                          bgcolor: "var(--cx-brand-button-hover-bg)",
                          borderColor: "var(--cx-brand-button-border)",
                        },
                      }}
                      type="button"
                      variant="outlined"
                    >
                      {activeScreen.backLabel ?? "Back"}
                    </Button>
                  ) : null}
                  <Button
                    fullWidth
                    onClick={goNext}
                    sx={{
                      bgcolor: "var(--cx-brand-primary)",
                      border: "1px solid",
                      borderColor: "var(--cx-brand-primary)",
                      color: "var(--cx-brand-primary-contrast)",
                      minHeight: 48,
                      "&:hover": { bgcolor: "var(--cx-brand-primary-dark)" },
                    }}
                    type="button"
                    variant="contained"
                  >
                    {activeScreen.nextLabel ??
                      (isLastScreen ? "Finish" : "Continue")}
                  </Button>
                </Stack>
                {activeScreen.secondaryActionLabel ? (
                  <Button
                    color="inherit"
                    onClick={() => {
                      if (activeScreen.secondaryActionTargetScreenId) {
                        goToScreen(activeScreen.secondaryActionTargetScreenId);
                        return;
                      }

                      goNext();
                    }}
                    sx={{
                      color: "var(--cx-brand-text-muted)",
                      minHeight: 32,
                      py: 0,
                    }}
                    type="button"
                    variant="text"
                  >
                    {activeScreen.secondaryActionLabel}
                  </Button>
                ) : (
                  <PoweredByApruv compact sx={{ pb: 0 }} />
                )}
              </Stack>
            </Card>
          ) : null}
        </Stack>
      </Box>
    </CompanyBrandProvider>
  );
};

export default FlowRenderer;
